import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TagIcon from "@mui/icons-material/Tag";

/**
 * Tracker Identifier column display. Non-pill options:
 * - "inline": icon + text, no container (default)
 * - "code": monospace text in a subtle rounded rectangle (inline-code style)
 * - "minimal": text only, no icon
 */
export default function TrackerIdentifierPill({ value, variant = "inline" }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const displayValue =
    value != null && String(value).trim() !== "" ? String(value).trim() : "—";
  const hasValue = displayValue !== "—";

  const textColor = hasValue
    ? isDark
      ? "rgba(226, 232, 240, 0.95)"
      : theme.palette.text.primary
    : isDark
      ? "rgba(255,255,255,0.45)"
      : theme.palette.text.secondary;

  const iconColor = hasValue
    ? isDark
      ? "rgba(148, 163, 184, 0.7)"
      : "rgba(100, 116, 139, 0.75)"
    : textColor;
  const greenColor = theme.palette.success?.main || "#A0C46D";

  if (variant === "minimal") {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          alignItems: "center",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: textColor,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            transition: "border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
            "&:focus-visible": {
              outline: "none",
              boxShadow: `0 0 0 2px ${greenColor}40`,
            },
            "@media (prefers-reduced-motion: reduce)": {
              transition: "none",
            },
          }}
        >
          {displayValue}
        </Box>
      </Box>
    );
  }

  if (variant === "code") {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          alignItems: "center",
          minWidth: 0,
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-block",
            fontFamily: "ui-monospace, monospace",
            fontSize: "0.75rem",
            fontWeight: 500,
            color: textColor,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
            px: 1,
            py: 0.25,
            borderRadius: "6px",
            backgroundColor: isDark
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.04)",
            border: isDark
              ? "1px solid rgba(148, 163, 184, 0.12)"
              : "1px solid rgba(0,0,0,0.06)",
            transition: "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
            "&:focus-visible": {
              outline: "none",
              boxShadow: `0 0 0 2px ${greenColor}40`,
            },
            "@media (prefers-reduced-motion: reduce)": {
              transition: "none",
            },
          }}
        >
          {displayValue}
        </Box>
      </Box>
    );
  }

  // default: inline (icon + text, no box)
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        alignItems: "center",
        minWidth: 0,
        gap: 0.75,
      }}
    >
      <TagIcon
        sx={{
          fontSize: "0.875rem",
          color: iconColor,
          flexShrink: 0,
          mt: "1px",
        }}
      />
      <Box
        component="span"
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 500,
          color: textColor,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
          transition: "color 0.2s ease",
          "&:focus-visible": {
            outline: "none",
            boxShadow: `0 0 0 2px ${greenColor}40`,
            borderRadius: "4px",
          },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
        }}
      >
        {displayValue}
      </Box>
    </Box>
  );
}
